import * as THREE from 'three';
import { glass, glow, material, plain } from './textures.js';
import { MAP } from '../../config.js';

/**
 * CG の部屋に置く家具。すべて箱・円柱・球の組み合わせで作る。
 * 各関数は原点が「家具の足元の中心」のグループを返す。
 * 設計データ側で位置と向きを決める（buildHouse.js）。
 */

const WHITE = 0xf4f2ee;
const BLACK = 0x1f1f1f;
const DARK_WOOD = 0x5a3d26;
const OAK = 0xc9a575;
const CREAM = 0xefe4d0;

/** 箱を「下面の中心」を基準に置く */
function box(group, w, h, d, mat, x = 0, y = 0, z = 0, rotY = 0) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, y + h / 2, z);
  mesh.rotation.y = rotY;
  group.add(mesh);
  return mesh;
}

function cylinder(group, r, h, mat, x = 0, y = 0, z = 0, rTop = r, segments = 24) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rTop, r, h, segments), mat);
  mesh.position.set(x, y + h / 2, z);
  group.add(mesh);
  return mesh;
}

/** 色を白へ寄せる（同じ布の陰影を作るのに使う） */
function lighten(hex, amount) {
  const c = new THREE.Color(hex);
  c.lerp(new THREE.Color(0xffffff), amount);
  return c.getHex();
}

function sphere(group, r, mat, x, y, z, sx = 1, sy = 1, sz = 1) {
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(r, 16, 12), mat);
  mesh.position.set(x, y, z);
  mesh.scale.set(sx, sy, sz);
  group.add(mesh);
  return mesh;
}

export const FURNITURE = {
  /** ただの箱（収納・パネル・サイドボードなど） */
  box({ w = 1, h = 1, d = 1, mat = 'walnut', color }) {
    const g = new THREE.Group();
    box(g, w, h, d, color !== undefined ? plain(color) : material(mat, w, h));
    return g;
  },

  /** 畳の小上がり。天板は畳、側面は木。手前(+z)に段を付ける */
  platform({ w = 3, d = 3.7, h = 0.35 }) {
    const g = new THREE.Group();
    const side = material('walnut', w, h);
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [
      side, side, material('tatami', w, d), side, side, side,
    ]);
    mesh.position.set(0, h / 2, 0);
    g.add(mesh);
    // 縁の木枠
    box(g, w + 0.06, 0.03, d + 0.06, plain(DARK_WOOD), 0, h - 0.03, 0);
    // 段
    box(g, w * 0.8, h / 2, 0.3, material('walnut', w, 0.3), 0, 0, d / 2 + 0.15);
    // 足元の間接照明
    box(g, w + 0.02, 0.02, d + 0.02, glow(0xffd9a0, 1.2), 0, 0.02, 0);
    return g;
  },

  /** 掘りごたつ。天板の下が掘り込まれている */
  kotatsu({ w = 1.6, d = 0.85 }) {
    const g = new THREE.Group();
    box(g, w + 0.6, 0.02, d + 0.9, plain(0x2a2116), 0, 0, 0);
    box(g, w, 0.06, d, material('oakLight', w, d), 0, 0.34, 0);
    box(g, w - 0.2, 0.34, d - 0.2, plain(0x3b2c1c), 0, 0, 0);
    return g;
  },

  /** 座布団 */
  zabuton() {
    const g = new THREE.Group();
    cylinder(g, 0.23, 0.07, plain(CREAM), 0, 0, 0);
    return g;
  },

  /** ダイニングテーブル。中央に濃い色のランナー */
  table({ w = 2.1, d = 0.9, h = 0.72, runner = true }) {
    const g = new THREE.Group();
    box(g, w, 0.05, d, material('oakLight', w, d), 0, h - 0.05, 0);
    if (runner) box(g, w * 0.6, 0.01, d * 0.4, plain(0x2b2b2b), 0, h, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      box(g, 0.07, h - 0.05, 0.07, plain(OAK), sx * (w / 2 - 0.1), 0, sz * (d / 2 - 0.1));
    }
    return g;
  },

  /** 木の椅子。背もたれ付き。-z が背 */
  chair() {
    const g = new THREE.Group();
    box(g, 0.44, 0.04, 0.44, plain(OAK), 0, 0.43, 0);
    box(g, 0.40, 0.04, 0.40, plain(CREAM), 0, 0.47, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      box(g, 0.035, 0.43, 0.035, plain(OAK), sx * 0.19, 0, sz * 0.19);
    }
    for (const sx of [-1, 1]) box(g, 0.035, 0.36, 0.035, plain(OAK), sx * 0.19, 0.47, -0.19);
    box(g, 0.44, 0.07, 0.04, plain(OAK), 0, 0.76, -0.19);
    return g;
  },

  /** 2人掛けの低い布張りソファ。-z が背 */
  sofa({ color = 0x50545b } = {}) {
    const g = new THREE.Group();
    const fabric = plain(color);
    // 座面のクッションは本体より少し明るくして、面の切れ目を分かるようにする
    const cushion = plain(lighten(color, 0.12));
    box(g, 1.65, 0.28, 0.78, fabric, 0, 0.12, 0.05);
    box(g, 1.65, 0.62, 0.16, fabric, 0, 0.25, -0.34);
    box(g, 0.72, 0.12, 0.62, cushion, -0.39, 0.4, 0.06);
    box(g, 0.72, 0.12, 0.62, cushion, 0.39, 0.4, 0.06);
    for (const x of [-0.65, 0.65]) {
      box(g, 0.07, 0.12, 0.07, plain(BLACK), x, 0, -0.22);
      box(g, 0.07, 0.12, 0.07, plain(BLACK), x, 0, 0.28);
    }
    return g;
  },

  /** スツール（黒） */
  stool() {
    const g = new THREE.Group();
    cylinder(g, 0.17, 0.04, plain(BLACK), 0, 0.62, 0);
    for (let i = 0; i < 3; i += 1) {
      const a = (i / 3) * Math.PI * 2;
      cylinder(g, 0.012, 0.62, plain(BLACK), Math.cos(a) * 0.12, 0, Math.sin(a) * 0.12);
    }
    return g;
  },

  /** ベッド。枕は -z 側（頭）。w=幅 d=長さ */
  bed({ w = 1.0, d = 2.0, low = false }) {
    const g = new THREE.Group();
    const base = low ? 0.12 : 0.25;
    box(g, w, base, d, material('walnut', w, d), 0, 0, 0);
    box(g, w - 0.04, 0.22, d - 0.04, material('linen', w, d), 0, base, 0);
    box(g, w - 0.02, 0.04, d * 0.45, material('blanket', w, d), 0, base + 0.22, d * 0.2);
    for (const sx of w > 1.3 ? [-0.25, 0.25] : [0]) {
      sphere(g, 0.2, plain(WHITE), sx * w, base + 0.28, -d / 2 + 0.3, 1.1, 0.35, 0.6);
    }
    return g;
  },

  /** 浴槽（白）。5枚の板で内側をくり抜く */
  bathtub({ w = 1.6, d = 0.75, h = 0.55 }) {
    const g = new THREE.Group();
    const m = plain(WHITE);
    const t = 0.07;
    box(g, w, 0.08, d, m, 0, 0, 0);
    box(g, w, h, t, m, 0, 0, -d / 2 + t / 2);
    box(g, w, h, t, m, 0, 0, d / 2 - t / 2);
    box(g, t, h, d, m, -w / 2 + t / 2, 0, 0);
    box(g, t, h, d, m, w / 2 - t / 2, 0, 0);
    return g;
  },

  /** トイレ。タンクは -z 側 */
  toilet() {
    const g = new THREE.Group();
    const m = plain(WHITE);
    box(g, 0.26, 0.38, 0.4, m, 0, 0, 0.02);
    sphere(g, 0.2, m, 0, 0.4, 0.08, 1.0, 0.25, 1.35);
    box(g, 0.4, 0.4, 0.16, m, 0, 0.38, -0.25);
    box(g, 0.34, 0.03, 0.42, plain(0xffffff), 0, 0.42, 0.06);
    return g;
  },

  /** 洗面台。木のキャビネットに白いボウル。鏡は別 */
  basin({ w = 0.9, d = 0.5 }) {
    const g = new THREE.Group();
    box(g, w, 0.72, d, material('walnut', w, 0.72), 0, 0, 0);
    box(g, w + 0.02, 0.1, d + 0.02, plain(WHITE), 0, 0.72, 0);
    box(g, w * 0.7, 0.02, d * 0.6, plain(0xe0dcd4), 0, 0.82, 0);
    cylinder(g, 0.015, 0.18, plain(0xb8bcc0), 0, 0.8, -d / 2 + 0.08);
    return g;
  },

  /** 洗濯機 */
  washer() {
    const g = new THREE.Group();
    box(g, 0.6, 0.9, 0.6, plain(WHITE), 0, 0, 0);
    const door = cylinder(g, 0.2, 0.02, plain(0x2b2f33), 0, 0.4, 0.31);
    door.rotation.x = Math.PI / 2;
    return g;
  },

  /** 鏡。壁に貼る（-z が壁側） */
  mirror({ w = 0.6, h = 0.6 }) {
    const g = new THREE.Group();
    box(g, w, h, 0.02, new THREE.MeshPhongMaterial({ color: 0xc9d3d8, shininess: 120, specular: 0xffffff }), 0, 0, 0);
    return g;
  },

  /** 棚板 */
  shelf({ w = 0.6, d = 0.25 }) {
    const g = new THREE.Group();
    box(g, w, 0.03, d, plain(WHITE), 0, 0, 0);
    return g;
  },

  /** 寝椅子（黒革）。-z が頭側 */
  chaise() {
    const g = new THREE.Group();
    const leather = plain(0x151515);
    const chrome = plain(0x8a8f94);
    const seat = box(g, 0.6, 0.12, 1.1, leather, 0, 0.3, 0.15);
    seat.rotation.x = 0.18;
    const back = box(g, 0.6, 0.12, 0.7, leather, 0, 0.42, -0.55);
    back.rotation.x = -0.55;
    sphere(g, 0.12, leather, 0, 0.75, -0.8, 2.4, 1, 1);
    cylinder(g, 0.02, 0.5, chrome, 0.25, 0, 0.35);
    cylinder(g, 0.02, 0.5, chrome, -0.25, 0, 0.35);
    box(g, 0.7, 0.03, 0.3, chrome, 0, 0, -0.5);
    return g;
  },

  /** 観葉植物。size で大きさ */
  plant({ size = 1 }) {
    const g = new THREE.Group();
    cylinder(g, 0.16 * size, 0.28 * size, plain(0x2b2b2b), 0, 0, 0, 0.14 * size);
    cylinder(g, 0.02, 0.9 * size, plain(0x5a4630), 0, 0.28 * size, 0);
    const leaf = plain(0x4f8a3d);
    for (let i = 0; i < 5; i += 1) {
      const a = i * 2.1;
      sphere(g, 0.22 * size, leaf, Math.cos(a) * 0.18 * size, (0.95 + (i % 3) * 0.18) * size, Math.sin(a) * 0.18 * size);
    }
    return g;
  },

  /** ペンダント照明（黒い笠）。y は天井の高さ、drop で吊り下げ長さ */
  pendant({ drop = 0.9 }) {
    const g = new THREE.Group();
    cylinder(g, 0.006, drop, plain(BLACK), 0, -drop, 0);
    const shade = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.16, 20, 1, true), plain(BLACK, { side: THREE.DoubleSide }));
    shade.position.y = -drop - 0.08;
    g.add(shade);
    sphere(g, 0.035, glow(0xffe2b0, 1.6), 0, -drop - 0.12, 0);
    return g;
  },

  /** 和紙の提灯 */
  lantern({ drop = 0.9 }) {
    const g = new THREE.Group();
    cylinder(g, 0.006, drop, plain(BLACK), 0, -drop, 0);
    sphere(g, 0.18, glow(0xfff1d8, 1.3), 0, -drop - 0.15, 0, 1, 0.8, 1);
    return g;
  },

  /** スポットライトのレール。y は天井の高さ */
  spotRail({ len = 1.2 }) {
    const g = new THREE.Group();
    box(g, len, 0.03, 0.03, plain(BLACK), 0, -0.03, 0);
    for (let i = 0; i < 4; i += 1) {
      const x = -len / 2 + (len / 4) * (i + 0.5);
      cylinder(g, 0.045, 0.1, plain(BLACK), x, -0.15, 0, 0.035, 12);
      sphere(g, 0.028, glow(0xffe4b8, 1.5), x, -0.15, 0);
    }
    return g;
  },

  /** 柱 */
  post({ h = 2.7, size = 0.12 }) {
    const g = new THREE.Group();
    box(g, size, h, size, material('walnut', size, h));
    return g;
  },

  /** 梁（x 方向に len） */
  beam({ len = 3, size = 0.16, y = 2.4 }) {
    const g = new THREE.Group();
    box(g, len, size, size, material('walnut', len, size), 0, y, 0);
    return g;
  },

  /** キッチン。x 方向に len。壁は -z 側 */
  kitchen({ len = 2.4 }) {
    const g = new THREE.Group();
    const dark = plain(0x2a2b2d);
    box(g, len, 0.85, 0.62, dark, 0, 0, 0.31);
    box(g, len, 0.04, 0.64, plain(WHITE), 0, 0.85, 0.32);
    box(g, 0.6, 0.02, 0.4, plain(0x8f9498), -len * 0.22, 0.88, 0.3);
    box(g, 0.55, 0.01, 0.4, plain(0x111111), len * 0.25, 0.89, 0.3);
    box(g, len * 0.55, 0.7, 0.35, dark, -len * 0.22, 1.5, 0.18);
    box(g, len * 0.4, 0.5, 0.5, plain(WHITE), len * 0.28, 1.6, 0.26);
    for (let i = 0; i < 4; i += 1) box(g, 0.02, 0.15, 0.02, plain(0xd0d0d0), -len / 2 + 0.3 + i * 0.5, 0.45, 0.63);
    return g;
  },

  /** 角の丸いカウンター（キッチンのテーブル）。x 方向に len */
  counter({ len = 1.4, d = 0.7, h = 0.95 }) {
    const g = new THREE.Group();
    const top = plain(0x7a6656);
    box(g, len - d, 0.05, d, top, 0, h - 0.05, 0);
    cylinder(g, d / 2, 0.05, top, -(len - d) / 2, h - 0.05, 0);
    cylinder(g, d / 2, 0.05, top, (len - d) / 2, h - 0.05, 0);
    cylinder(g, 0.04, h - 0.05, plain(BLACK), (len - d) / 2, 0, 0);
    box(g, 0.25, h - 0.05, 0.4, plain(0x4a3d33), -(len - d) / 2, 0, 0);
    return g;
  },

  /** サウナのストーブ（石入り） */
  heater() {
    const g = new THREE.Group();
    box(g, 0.45, 0.7, 0.45, plain(0x333333), 0, 0, 0);
    for (let i = 0; i < 6; i += 1) sphere(g, 0.07, plain(0x6d6a66), (i % 3 - 1) * 0.12, 0.74, (i < 3 ? -1 : 1) * 0.08);
    return g;
  },

  /** サウナのベンチ（x 方向に len） */
  bench({ len = 1.5, h = 0.45, d = 0.5 }) {
    const g = new THREE.Group();
    box(g, len, 0.05, d, material('cedar', len, d), 0, h - 0.05, 0);
    box(g, len, h - 0.05, 0.05, material('cedar', len, h), 0, 0, d / 2 - 0.03);
    return g;
  },

  /** 額縁。-z が壁側 */
  picture({ w = 0.5, h = 0.6, color = 0x1a1a1a }) {
    const g = new THREE.Group();
    box(g, w, h, 0.03, plain(0x2b2b2b), 0, 0, 0);
    box(g, w - 0.06, h - 0.06, 0.005, plain(color), 0, 0.03, 0.02);
    return g;
  },

  /** エアコン。-z が壁側 */
  ac() {
    const g = new THREE.Group();
    box(g, 0.8, 0.28, 0.24, plain(WHITE), 0, 0, 0.12);
    return g;
  },

  /** 引き戸のガラス扉（枠付き）。x 方向に w */
  glassDoor({ w = 1.6, h = 2.1, panels = 2 }) {
    const g = new THREE.Group();
    const frame = plain(0xe8e8e8);
    box(g, w, 0.04, 0.05, frame, 0, h - 0.04, 0);
    box(g, w, 0.02, 0.05, frame, 0, 0, 0);
    for (let i = 0; i <= panels; i += 1) box(g, 0.04, h, 0.05, frame, -w / 2 + (w / panels) * i, 0, 0);
    box(g, w, h, 0.006, glass(), 0, 0, 0);
    return g;
  },

  /** 襖（閉じた状態）。x 方向に w */
  fusuma({ w = 1.7, h = 2.2 }) {
    const g = new THREE.Group();
    box(g, w, h, 0.03, material('fusuma', w / 0.85, 1), 0, 0, 0);
    box(g, w, 0.04, 0.06, plain(DARK_WOOD), 0, h, 0);
    box(g, w, 0.04, 0.06, plain(DARK_WOOD), 0, 0, 0);
    return g;
  },

  /** 縦格子の壁パネル。x 方向に w */
  slatPanel({ w = 2, h = 1.6 }) {
    const g = new THREE.Group();
    box(g, w, h, 0.05, material('slats', w / 0.6, 1), 0, 0, 0);
    box(g, w + 0.02, 0.02, 0.06, glow(0xffd9a0, 1.2), 0, h, 0);
    return g;
  },

  /** テーブルランプ */
  lamp() {
    const g = new THREE.Group();
    cylinder(g, 0.06, 0.18, plain(0x6b6b6b), 0, 0, 0, 0.05);
    cylinder(g, 0.1, 0.12, glow(0xffe8c8, 1.2), 0, 0.18, 0, 0.09);
    return g;
  },

  /** サイドテーブル（小さな丸） */
  sideTable() {
    const g = new THREE.Group();
    cylinder(g, 0.22, 0.02, plain(BLACK), 0, 0.5, 0);
    cylinder(g, 0.015, 0.5, plain(BLACK), 0, 0, 0);
    return g;
  },

  /** オットマン */
  ottoman({ color = 0xb08a80 } = {}) {
    const g = new THREE.Group();
    cylinder(g, 0.25, 0.4, plain(color), 0, 0, 0);
    return g;
  },

  /** ロールスクリーン（壁に付ける、-z が壁側） */
  blind({ w = 2.2, h = 1.0 }) {
    const g = new THREE.Group();
    box(g, w, h, 0.03, material('linen', w, h), 0, 0, 0.03);
    return g;
  },

  /** 壁の間接照明の帯 */
  coveLight({ len = 2 }) {
    const g = new THREE.Group();
    box(g, len, 0.03, 0.08, glow(0xffdcaa, 1.3), 0, 0, 0);
    return g;
  },

  /** 冷蔵庫（フレンチドア）。扉は +z 側 */
  fridge({ w = 0.75, h = 1.83, d = 0.7 }) {
    const g = new THREE.Group();
    const body = plain(0x3c3f44);
    const door = plain(0x6f757c);
    box(g, w, h, d, body, 0, 0, 0);
    // 上段は観音開き、下段は引き出し3段
    for (const sx of [-1, 1]) box(g, w / 2 - 0.01, h * 0.52, 0.02, door, (sx * w) / 4, h * 0.48, d / 2);
    for (let i = 0; i < 3; i += 1) box(g, w - 0.02, h * 0.15, 0.02, door, 0, i * h * 0.16 + 0.02, d / 2);
    for (const sx of [-1, 1]) box(g, 0.02, h * 0.4, 0.03, plain(0xc8ccd0), sx * 0.04, h * 0.55, d / 2 + 0.02);
    return g;
  },

  /** 薄型テレビ。画面は +z 側。w は画面の幅（55型で約1.23m） */
  tv({ w = 1.23, h = 0.71 }) {
    const g = new THREE.Group();
    box(g, w, h, 0.06, plain(BLACK), 0, 0, 0);
    box(g, w - 0.03, h - 0.03, 0.01, plain(0x0b0d10), 0, 0.015, 0.035);
    return g;
  },

  /** テレビの壁寄せスタンド（ロータイプ）。テレビはこの上に別途置く */
  tvStand({ h = 0.75, w = 0.62 }) {
    const g = new THREE.Group();
    const steel = plain(0x2c2e31);
    box(g, w, 0.04, 0.4, steel, 0, 0, 0);
    box(g, 0.08, h, 0.08, steel, 0, 0.04, 0);
    box(g, 0.5, 0.03, 0.28, plain(0x4a4d52), 0, h * 0.35, 0.05);
    return g;
  },

  /** ドレッサー（鏡付きの化粧台）。鏡は -z 側 */
  dresser({ w = 0.8, d = 0.4, h = 0.72 }) {
    const g = new THREE.Group();
    const wood = material('walnut', w, h);
    box(g, w, h, d, wood, 0, 0, 0);
    for (let i = 0; i < 2; i += 1) box(g, w - 0.06, h * 0.4, 0.02, plain(0x8a6a4c), 0, 0.05 + i * h * 0.45, d / 2);
    // 鏡
    box(g, w * 0.7, 0.62, 0.03, wood, 0, h, -d / 2 + 0.05);
    box(g, w * 0.62, 0.54, 0.01, plain(0xc9d3d8), 0, h + 0.04, -d / 2 + 0.07);
    return g;
  },

  /** スチールラック（備品庫の棚） */
  steelRack({ w = 0.9, d = 0.45, h = 1.8, shelves = 5 }) {
    const g = new THREE.Group();
    const steel = plain(0x9aa0a6);
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        box(g, 0.03, h, 0.03, steel, (sx * (w - 0.03)) / 2, 0, (sz * (d - 0.03)) / 2);
      }
    }
    for (let i = 0; i < shelves; i += 1) {
      box(g, w, 0.02, d, steel, 0, (i * (h - 0.05)) / (shelves - 1), 0);
    }
    return g;
  },

  /** 掛け軸式のプロジェクタースクリーン。壁に掛ける（-z が壁側） */
  screen({ w = 2.21, h = 1.24 }) {
    const g = new THREE.Group();
    box(g, w + 0.06, 0.05, 0.04, plain(0x2a2c2f), 0, h, 0);
    box(g, w, h, 0.01, plain(0xf6f6f4), 0, 0, 0.01);
    box(g, w + 0.06, 0.04, 0.04, plain(0x2a2c2f), 0, -0.04, 0);
    return g;
  },

  /** 小型プロジェクター（円筒型） */
  projector() {
    const g = new THREE.Group();
    cylinder(g, 0.04, 0.17, plain(0x35383d), 0, 0, 0);
    cylinder(g, 0.028, 0.01, glow(0xdfe8ff, 0.6), 0, 0.17, 0);
    return g;
  },

  /**
   * 白物家電のひとまとめ。レンジ・炊飯器・トースター・加湿器・空気清浄機・除湿機など、
   * 形が似ていて細かく作り分ける意味の薄いものをこれで置く。
   */
  appliance({ w = 0.5, h = 0.35, d = 0.4, color = WHITE, panel = true }) {
    const g = new THREE.Group();
    box(g, w, h, d, plain(color), 0, 0, 0);
    if (panel) box(g, w * 0.7, h * 0.45, 0.01, plain(0x24262a), 0, h * 0.3, d / 2);
    return g;
  },
};

/**
 * スキャンから拾った家具のかたまり。
 * 何の家具かは分からないので、スキャンの色を付けた小さな箱の集まりとして置く（1回の描画でまとめて描く）。
 * cells: [{ gx, gz, bottom, top, color }]、cell: マスの大きさ(m)、minX/minZ: マス番号0の位置
 */
FURNITURE.cluster = function cluster({ cells, cell, minX, minZ }) {
  const g = new THREE.Group();
  const geometry = new THREE.BoxGeometry(cell, 1, cell);
  const mesh = new THREE.InstancedMesh(geometry, new THREE.MeshLambertMaterial(), cells.length);
  const matrix = new THREE.Matrix4();
  const color = new THREE.Color();
  cells.forEach((it, i) => {
    const h = Math.max(it.top - it.bottom, 0.02);
    matrix.makeScale(1, h, 1);
    matrix.setPosition((it.gx + 0.5) * cell + minX, it.bottom + h / 2, (it.gz + 0.5) * cell + minZ);
    mesh.setMatrixAt(i, matrix);
    // スキャンの色は暗く沈みがちなので少し持ち上げる。
    // 真っ黒に近い色は床の穴のように見えてしまうので、下限を設ける
    color.setHex(it.color);
    const floor = MAP.furnitureMinBrightness;
    mesh.setColorAt(
      i,
      color.setRGB(
        Math.min(1, Math.max(floor, color.r * MAP.colorBoost)),
        Math.min(1, Math.max(floor, color.g * MAP.colorBoost)),
        Math.min(1, Math.max(floor, color.b * MAP.colorBoost)),
      ),
    );
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
  g.add(mesh);
  return g;
};

/** 設計データの1件から家具を作り、位置と向きを付ける */
export function makeFurniture(item) {
  const maker = FURNITURE[item.type];
  if (typeof maker !== 'function') throw new Error(`家具 ${item.type} はありません`);
  const group = maker(item);
  group.position.set(item.x ?? 0, item.y ?? 0, item.z ?? 0);
  group.rotation.y = ((item.rot ?? 0) * Math.PI) / 180;
  return group;
}
